import {
  platformAuthenticatorIsAvailable,
  startAuthentication,
  PublicKeyCredentialRequestOptionsJSON,
  browserSupportsWebAuthnAutofill,
  browserSupportsWebAuthn,
  AuthenticationResponseJSON,
  WebAuthnError,
} from '@simplewebauthn/browser';

export const changeErrorVisibility = (e: HTMLElement, errorId: string | boolean) => {
  const errorEls = e?.closest('.field')?.querySelectorAll('ptrn-field-error') as NodeListOf<HTMLElement>;
  const ptrnFieldEl = e?.parentElement;

  if (typeof errorId === 'string') {
    errorEls.forEach(error => error.hidden = (error.id !== errorId));
    ptrnFieldEl?.classList.toggle('field--error', true);
  } else {
    errorEls.forEach(error => error.hidden = true);
    ptrnFieldEl?.classList.toggle('field--error', false);
  }
}

export const setButtonLoading = (e?: HTMLButtonElement) => {
  if (e && !e.classList.contains('button--loading')) {
    e.classList.toggle('button--loading', true);
  }
}

function getPasskeyOptions() {
  const optionsElement = document.getElementById('passkey-options') as HTMLScriptElement;
  if (!optionsElement) {
    throw new Error("Passkey options not found");
  }

  return JSON.parse(optionsElement.innerText) as PublicKeyCredentialRequestOptionsJSON;
}

function submitPasskeyAssertion(assertion: AuthenticationResponseJSON, interaction: 'autofill'|'prompt') {
  const interactionInput = document.getElementById('passkeyInteraction') as HTMLInputElement;
  if (interactionInput) {
    interactionInput.value = interaction;
  }

  const assertionElement = document.getElementById('passkeyAssertion') as HTMLInputElement;
  if (!assertionElement.form) {
    throw new Error("Passkey assertion form not found");
  }

  assertionElement.value = JSON.stringify(assertion);
  assertionElement.form.submit();
}

export const startPasskeyAutofill = async () => {
  if (!await browserSupportsWebAuthnAutofill()) {
    console.warn("Conditional UI not implemented");
    return;
  }

  if (!await platformAuthenticatorIsAvailable()) {
    console.warn("User verification method for conditional UI not available");
    return;
  }

  try {
    const optionsJSON = getPasskeyOptions();
    const assertion = await startAuthentication({ optionsJSON, useBrowserAutofill: true });
    submitPasskeyAssertion(assertion, 'autofill');
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      console.warn(e);
    } else {
      console.error("Conditional UI failed to start authentication", e);
    }
  }
}

export const startPasskeyPrompt = async () => {
  if (!browserSupportsWebAuthn()) {
    throw new Error("WebAuthn not supported");
  }

  console.info("prompting for passkey");
  const optionsJSON = getPasskeyOptions();
  const assertion = await startAuthentication({ optionsJSON });
  submitPasskeyAssertion(assertion, 'prompt');
}

export const tryPasskeyPrompt = async (passkeyButton: HTMLButtonElement, passkeyError?: HTMLInputElement) => {
  if (!browserSupportsWebAuthn()) {
    changeErrorVisibility(passkeyButton, 'passkeyNotSupported');
    return;
  }

  setButtonLoading(passkeyButton);

  try {
    await startPasskeyPrompt();
  } catch (e) {
    if (e instanceof Error) {
      // Server-side logging
      if (passkeyError) {
        let detail = '';
        if (e instanceof WebAuthnError && e.code === 'ERROR_INVALID_RP_ID') {
          detail = location.hostname;
        } else if (e.cause instanceof Error && !(e instanceof WebAuthnError && e.code === 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY')) {
          detail = e.cause.message;
        }
        passkeyError.value = `${e.name}: ${e.message}` + (detail ? ` (${detail})` : '');
      }

      // Client handling
      if (e.name === 'NotSupportedError') {
        console.warn(e);
        changeErrorVisibility(passkeyButton, 'passkeyNotSupported');
      } else if (e.name === 'AbortError' || e.name === 'NotAllowedError') {
        console.warn(e);
        changeErrorVisibility(passkeyButton, 'passkeyCancelled');
      } else {
        console.error(e);
        changeErrorVisibility(passkeyButton, 'passkeyFailed');
      }
    }

    passkeyButton.classList.remove('button--loading');
  }
}

export const usePasskeyButton = () => {
  const passkeyErrorInput = document.getElementById('passkeyError') as HTMLInputElement;
  const passkeyButton = document.getElementById('usePasskeyButton') as HTMLButtonElement;
  if (passkeyButton) {
    passkeyButton.addEventListener('click', async (event) => {
      event.preventDefault();
      await tryPasskeyPrompt(passkeyButton, passkeyErrorInput);
    });
  }
}

export const usePasskeyPageLink = () => {
  const passkeyLink = document.getElementById('usePasskeyPageLink') as HTMLButtonElement;
  if (passkeyLink) {
    passkeyLink.addEventListener('click', async (event) => {
      event.preventDefault();
      const href = passkeyLink.getAttribute('data-href');
      if (href) {
        if (!browserSupportsWebAuthn()) {
          changeErrorVisibility(passkeyLink, 'passkeyNotSupported');
          return;
        }

        window.location.href = href;
      }
    });
  }
}